import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { IconText } from 'components/core/IconText';
import { H2, H3 } from 'components/core/Headline';

import './FacilityAmenitiesRanked.scss';

// import all Amenity icons
import twentyfourhouraccess from 'assets/images/features-amenities/24-hr-access.svg';
import boat from 'assets/images/features-amenities/boat.svg';
import car from 'assets/images/features-amenities/car.svg';
import climatecontrolled from 'assets/images/features-amenities/climate-controlled.svg';
import driveupaccess from 'assets/images/features-amenities/drive-up-access.svg';
import fencedlit from 'assets/images/features-amenities/fenced-lit.svg';
import firstflooraccess from 'assets/images/features-amenities/first-floor-access.svg';
import freeuseoftruck from 'assets/images/features-amenities/free-use-of-truck.svg';
import onsiteoffice from 'assets/images/features-amenities/on-site-office.svg';
import residentmanager from 'assets/images/features-amenities/resident-manager.svg';
import rv from 'assets/images/features-amenities/rv.svg';
import surveillancecameras from 'assets/images/features-amenities/surveillance-cameras.svg';

// Define the ranking order for the icon and text display
const AMENITY_CONFIG = [
	{ key: '24HrAccess', icon: twentyfourhouraccess, text: '24-hour access', alt: '24-hour-access-icon' },
	{ key: 'onsiteOfficeAtFacility', icon: onsiteoffice, text: 'On-site office', alt: 'On-site-office-icon' },
	{ key: 'surveillance', icon: surveillancecameras, text: 'Video cameras on site', alt: 'Video-cameras-on-site-icon' },
	{ key: 'fenced', icon: fencedlit, text: 'Fenced & Lighted', alt: 'Fenced-&-Lighted-icon' },
	{ key: 'freeTruckRental', icon: freeuseoftruck, text: 'Free use of truck', alt: 'Free-use-of-truck-icon' },
	{ key: 'residentManager', icon: residentmanager, text: 'Resident manager', alt: 'Resident-manager-icon' },
	{ key: 'climateControlled', icon: climatecontrolled, text: 'Climate controlled units', alt: 'Climate-controlled-units-icon' },
	{ key: 'firstFloorAccess', icon: firstflooraccess, text: 'First floor access', alt: 'First-floor-access-icon' },
	{ key: 'driveUpAccess', icon: driveupaccess, text: 'Drive-up access', alt: 'Drive-up-access-icon' },
	{ key: 'vehicleStorage', icon: car, text: 'Vehicle Storage', alt: 'Vehicle-storage-icon' },
	{ key: 'boatStorage', icon: boat, text: 'Boat Storage', alt: 'Boat-storage-icon' },
	{ key: 'rvStorage', icon: rv, text: 'RV Storage', alt: 'RV-storage-icon' }
];

const ICON_MAPPING = Object.fromEntries(
	AMENITY_CONFIG.map(({ key, icon, text, alt }) => [key, { icon, text, alt }])
);

// Define storage type criteria - Boat, RV, Vehicle.
const STORAGE_TYPE_CRITERIA = {
	vehicleStorage: ['10x15-parking_space', '10x20-parking_space', '10x30-parking_space'],
	boatStorage: ['10x25-parking_space'],
	rvStorage: ['10x35-parking_space', '10x40-parking_space', '10x50-parking_space']
};

// Helper function to check storage type - Boat, RV, Vehicle.
const checkStorageType = (units, storageType) => {
	if (!units?.length) return false;
	
	return units.some(unit => {
		const sizeBucketUnitType = `${unit.sizeBucket}-${unit.unitType}`;
		
		// Special case for boat storage - also check if string includes 'boat'
		if (storageType === 'boatStorage') {
			return STORAGE_TYPE_CRITERIA[storageType].includes(sizeBucketUnitType) || 
				   sizeBucketUnitType.includes('boat');
		}
		
		return STORAGE_TYPE_CRITERIA[storageType].includes(sizeBucketUnitType);
	});
};

// Storage unit feature checkers
const checkStorageUnitFeatures = (storageUnits) => ({
	firstFloorAccess: storageUnits?.some(unit => unit.floor === 1) || false,
	driveUpAccess: storageUnits?.some(unit => unit.amenities?.collection?.driveUp?.value === true) || false,
	climateControlled: storageUnits?.some(unit => unit.amenities?.collection?.climateControlled?.value === true) || false
});

// Vehicle unit feature checkers
const checkVehicleUnitFeatures = (vehicleUnits) => ({
	vehicleStorage: checkStorageType(vehicleUnits, 'vehicleStorage'),
	boatStorage: checkStorageType(vehicleUnits, 'boatStorage'),
	rvStorage: checkStorageType(vehicleUnits, 'rvStorage'),
	firstFloorAccess: vehicleUnits?.some(unit => unit.floor === 1) || false,
	driveUpAccess: vehicleUnits?.some(unit => unit.amenities?.collection?.driveUp?.value === true) || false,
	climateControlled: vehicleUnits?.some(unit => unit.amenities?.collection?.climateControlled?.value === true) || false
});

export function FacilityAmenitiesRanked({ className, groupedAmenities = {}, storageUnits = [], vehicleUnits = [] }) {

	// Early return if no data
	if (!groupedAmenities && !storageUnits?.length && !vehicleUnits?.length) 
		return null;

	const availableAmenities = new Set();
	
	// Collect amenities from groupedAmenities (facility-level)
	if (groupedAmenities) {
		Object.values(groupedAmenities).forEach(category => {
			if (category?.items) {
				Object.entries(category.items).forEach(([key, item]) => {
					if (item?.hasAttribute && ICON_MAPPING[key]) {
						availableAmenities.add(key);
					}
				});
			}
		});
	}
	
	// Combine storageUnits and vehicleUnits feature logic
	const unitFeatures = storageUnits?.length ? checkStorageUnitFeatures(storageUnits) : {};
	const vehicleFeatures = vehicleUnits?.length ? checkVehicleUnitFeatures(vehicleUnits) : {};

	// Merge features: if any key is true, result is true
	const combinedFeatures = { ...unitFeatures };
	Object.entries(vehicleFeatures).forEach(([key, value]) => {
		combinedFeatures[key] = combinedFeatures[key] || value;
	});

	// Add available amenities
	Object.keys(combinedFeatures).forEach(amenityKey => {
		if (combinedFeatures[amenityKey] && ICON_MAPPING[amenityKey]) {
			availableAmenities.add(amenityKey);
		}
	});

	// Filter and map amenities in ranking order
	const amenityList = AMENITY_CONFIG
		.filter(({ key }) => availableAmenities.has(key))
		.map(({ key, icon, text, alt }) => ({ key, icon, text, alt }));

	// Don't render if no amenities to show
	if (amenityList.length === 0) return null;

	return (
		<div className={classnames(className, 'facility-amenities-ranked')}>
			<H2 className="section-heading">
				Features & Amenities
			</H2>
			{amenityList.map(({ key, icon, text }) => (
				<IconText key={key} src={icon} alt={text} size={"standard"} weight={"standard"} color={"black"} width="24" height="24">
					{text}
				</IconText>
			))}
		</div>
	);
}

FacilityAmenitiesRanked.propTypes = {
	className: PropTypes.string,
	groupedAmenities: PropTypes.object,
	storageUnits: PropTypes.array,
	vehicleUnits: PropTypes.array,
};

export default FacilityAmenitiesRanked;